import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid'; // uuid için import ekleyin
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import MainContent from './components/layout/MainContent';
import useSystemCalculations from './hooks/useSystemCalculations';
import './styles/animations.css';
import './styles/global.css'; // Global CSS ekledik

// Uygulama sürüm numarası - her güncelleme yaptığınızda artırın
const APP_VERSION = '1.1';
const VERSION_KEY = 'heat_pump_calculator_version';

const App = () => {
  const {
    systems,
    setSystems, // useSystemCalculations'dan setSystems'i alın
    darkMode,
    globalParams,
    activeAccordion,
    totalResults,
    setDarkMode,
    setActiveAccordion,
    removeSystem,
    handleGlobalParamChange,
    handleSystemParamChange
  } = useSystemCalculations();

  const [solarEnabled, setSolarEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // Versiyon kontrolü ve önbellek temizleme
  useEffect(() => {
    // Saklanan versiyon kontrolü
    const storedVersion = localStorage.getItem(VERSION_KEY);
    
    // Eğer stored version yoksa veya mevcut sürümden farklıysa
    if (storedVersion !== APP_VERSION) {
      console.log(`Uygulama güncellendi: ${storedVersion || 'Yeni kurulum'} → ${APP_VERSION}`);
      
      // Çerezleri temizle
      document.cookie.split(";").forEach(function(c) {
        document.cookie = c.replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      // LocalStorage öğelerini seçici olarak temizle
      // Korunacak öğeler listesi (tema ayarları gibi korunmasını istediğiniz öğeler)
      const protectedKeys = [VERSION_KEY, 'theme_preference'];
      
      Object.keys(localStorage).forEach(key => {
        if (!protectedKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Önbelleği temizleme
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      
      // Yeni sürümü kaydet
      localStorage.setItem(VERSION_KEY, APP_VERSION);
    }
  }, []);

  // Yeni sistem için varsayılan isim oluşturma fonksiyonu
  const getDefaultSystemName = (equipmentType, existingSystems) => {
    const count = existingSystems.filter(s => s.type === equipmentType.id).length + 1;
    return `${equipmentType.name} Sistemi ${count}`;
  };

  // Yeni sistem eklemek için fonksiyon
  const addSystem = (equipmentType) => {
    const newSystem = {
      id: uuidv4(),
      name: getDefaultSystemName(equipmentType, systems),
      type: equipmentType.id,
      heatingCapacity: 500,
      heatingTemperature: 60,
      coolingCapacity: 500,
      coolingTemperature: 20,
      chillerCop: 6.5,
      workingHours: 5000,
      clientEquipmentType: 'boiler', // Varsayılan olarak Boiler
      results: null
    };
    
    setSystems([...systems, newSystem]);
  };

  // Ekipman tıklama işleyicisi
  const handleEquipmentClick = (data) => {
    // Özel işlem tipini kontrol et
    if (data.action === 'updateClientEquipment') {
      // Belirli bir sistem ID'si varsa, o sistemi güncelle
      if (data.systemId) {
        const updatedSystems = systems.map(system => {
          if (system.id === data.systemId) {
            return {
              ...system,
              clientEquipmentType: data.equipmentType.id
            };
          }
          return system;
        });
        
        setSystems(updatedSystems);
      }
      // Sistem ID'si yoksa en son sistemi güncelle (geriye dönük uyumluluk için)
      else if (systems.length > 0) {
        const lastSystemIndex = systems.length - 1;
        const updatedSystems = [...systems];
        updatedSystems[lastSystemIndex] = {
          ...updatedSystems[lastSystemIndex],
          clientEquipmentType: data.equipmentType.id
        };
        setSystems(updatedSystems);
      }
      return;
    }
    
    // Normal ekipman ekleme işlemi
    addSystem(data);
  };

  // Ekran boyutunu izleyen fonksiyon
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sidebar görünürlüğünü değiştiren fonksiyon
  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  return (
    <div className={`min-h-screen ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-gray-950 to-black text-white' 
        : 'bg-gradient-to-br from-blue-50 via-white to-gray-100 text-gray-800'
    } p-8`}>
      <div className="max-w-full mx-auto">
        <Header 
          darkMode={darkMode} 
          setDarkMode={setDarkMode}
          isMobile={isMobile}
          toggleSidebar={toggleSidebar}
          appVersion={APP_VERSION} // Versiyon numarasını Header'a geçiriyoruz
        />
        
        {/* Sidebar - Mobil modda koşullu gösterim */}
        <div className={`${isMobile && !sidebarVisible ? 'hidden' : 'block'}`}>
          {isMobile && sidebarVisible && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-10"
              onClick={toggleSidebar}
            ></div>
          )}
          
          <div className={`${isMobile && sidebarVisible ? 'fixed left-0 top-0 h-full z-20 w-3/4 max-w-xs overflow-y-auto pt-16' : ''}`}>
            <Sidebar 
              darkMode={darkMode}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
              globalParams={globalParams}
              handleGlobalParamChange={handleGlobalParamChange}
              handleEquipmentClick={handleEquipmentClick}
              systems={systems} // Sistemleri geçiyoruz
              totalResults={totalResults}
              isMobile={isMobile}
              closeSidebar={() => setSidebarVisible(false)}
            />
          </div>
        </div>
        
        <MainContent 
          systems={systems}
          removeSystem={removeSystem}
          handleSystemParamChange={handleSystemParamChange}
          darkMode={darkMode}
          solarEnabled={solarEnabled}
          setSolarEnabled={setSolarEnabled}
          globalParams={globalParams}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

export default App;